import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  getSsoConfig,
  saveSsoConfig,
  activateSso,
  deactivateSso,
  testSso,
  deleteSsoConfig,
  getSpMetadata,
} from '../../services/sso';
import styles from './SsoConfigSection.module.css';

const STATUS_LABELS = {
  pending: 'Pending',
  active: 'Active',
  disabled: 'Disabled',
};

const STATUS_COLORS = {
  pending: '#e6a700',
  active: '#2d6a4f',
  disabled: '#c62828',
};

export default function SsoConfigSection({ orgId, ssoEnabled = true }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Form state
  const [protocol, setProtocol] = useState('saml');
  const [authMode, setAuthMode] = useState('exclusive');
  const [idpMetadataXml, setIdpMetadataXml] = useState('');
  const [idpMetadataUrl, setIdpMetadataUrl] = useState('');
  const [oidcDiscoveryUrl, setOidcDiscoveryUrl] = useState('');
  const [oidcClientId, setOidcClientId] = useState('');
  const [oidcClientSecret, setOidcClientSecret] = useState('');
  const [emailDomains, setEmailDomains] = useState('');
  const [attrMapping, setAttrMapping] = useState({
    email: '',
    firstName: '',
    lastName: '',
    extUid: '',
  });

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getSsoConfig(orgId);
      if (data.configured) {
        setConfig(data);
        setProtocol(data.protocol || 'saml');
        setAuthMode(data.authMode || 'exclusive');
        setIdpMetadataXml(data.idpMetadataXml || '');
        setIdpMetadataUrl(data.idpMetadataUrl || '');
        setOidcDiscoveryUrl(data.oidcDiscoveryUrl || '');
        setOidcClientId(data.oidcClientId || '');
        setOidcClientSecret('');
        setEmailDomains(Array.isArray(data.emailDomains) ? data.emailDomains.join(', ') : '');
        if (data.attributeMapping && typeof data.attributeMapping === 'object') {
          setAttrMapping({
            email: data.attributeMapping.email || '',
            firstName: data.attributeMapping.firstName || '',
            lastName: data.attributeMapping.lastName || '',
            extUid: data.attributeMapping.extUid || '',
          });
        }
      } else {
        setConfig(null);
      }
    } catch {
      setConfig(null);
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const showMessage = (msg) => {
    setMessage(msg);
    setError('');
  };
  const showError = (msg) => {
    setError(msg);
    setMessage('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const domains = emailDomains
        .split(',')
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean);

      const payload = {
        protocol,
        authMode,
        emailDomains: domains,
        attributeMapping: attrMapping,
      };

      if (protocol === 'saml') {
        if (idpMetadataXml) payload.idpMetadataXml = idpMetadataXml;
        if (idpMetadataUrl) payload.idpMetadataUrl = idpMetadataUrl;
      } else {
        payload.oidcDiscoveryUrl = oidcDiscoveryUrl;
        payload.oidcClientId = oidcClientId;
        if (oidcClientSecret) payload.oidcClientSecret = oidcClientSecret;
      }

      await saveSsoConfig(orgId, payload);
      showMessage('SSO configuration saved. Run a test connection before activating.');
      fetchConfig();
    } catch (err) {
      showError(err.response?.message || err.message || 'Failed to save SSO configuration.');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setError('');
    try {
      const { data } = await testSso(orgId);
      if (data.redirectUrl) {
        window.open(data.redirectUrl, '_blank');
        showMessage('Test opened in a new window. Click Refresh after completing the test.');
      }
    } catch (err) {
      showError(err.response?.message || err.message || 'Failed to start SSO test.');
    }
  };

  const handleActivate = async () => {
    setError('');
    try {
      await activateSso(orgId);
      showMessage('SSO activated successfully.');
      fetchConfig();
    } catch (err) {
      showError(
        err.response?.message || 'Failed to activate SSO. You must test the connection first.'
      );
    }
  };

  const handleDeactivate = async () => {
    setError('');
    try {
      await deactivateSso(orgId);
      showMessage('SSO deactivated.');
      fetchConfig();
    } catch (err) {
      showError(err.response?.message || 'Failed to deactivate SSO.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the SSO configuration?')) return;
    setError('');
    try {
      await deleteSsoConfig(orgId);
      showMessage('SSO configuration deleted.');
      setConfig(null);
      setIdpMetadataXml('');
      setIdpMetadataUrl('');
      setOidcDiscoveryUrl('');
      setOidcClientId('');
      setOidcClientSecret('');
      setEmailDomains('');
      setAttrMapping({ email: '', firstName: '', lastName: '', extUid: '' });
    } catch (err) {
      showError(err.response?.message || 'Failed to delete SSO configuration.');
    }
  };

  const handleDownloadMetadata = async () => {
    try {
      const { data } = await getSpMetadata();
      const blob = new Blob([data], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'know-sp-metadata.xml';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      showError('Failed to download SP metadata.');
    }
  };

  if (!ssoEnabled) {
    return (
      <details className={styles.ssoSection}>
        <summary>Single Sign-On (SSO)</summary>
        <div className={styles.content}>
          <p className={styles.message}>
            SSO is not enabled for this organization. Contact a KNOW administrator to enable this
            feature.
          </p>
        </div>
      </details>
    );
  }

  if (loading) return <p className={styles.loading}>Loading SSO configuration...</p>;

  const status = config?.status;
  const isActive = status === 'active';
  const isPending = status === 'pending';
  const isDisabled = status === 'disabled';

  return (
    <details className={styles.ssoSection}>
      <summary>Single Sign-On (SSO)</summary>
      <div className={styles.content}>
        <a
          href="/docs/sso-admin-guide"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.docsLink}
        >
          View Setup Guide &rarr;
        </a>

        {message && <p className={styles.message}>{message}</p>}
        {error && <p className={styles.error}>{error}</p>}

        {config && (
          <div className={styles.statusRow}>
            <span
              className={styles.statusBadge}
              style={{ backgroundColor: STATUS_COLORS[status] || '#666' }}
            >
              {STATUS_LABELS[status] || status}
            </span>
            {config.lastTestedAt && (
              <span className={styles.lastTested}>
                Last tested: {new Date(config.lastTestedAt).toLocaleString()}
              </span>
            )}
            <button type="button" className={styles.smallBtn} onClick={fetchConfig}>
              Refresh
            </button>
          </div>
        )}

        <div className={styles.formGroup}>
          <label>Protocol:</label>
          <div className={styles.radioRow}>
            <label>
              <input
                type="radio"
                name="protocol"
                value="saml"
                checked={protocol === 'saml'}
                onChange={() => setProtocol('saml')}
              />
              SAML 2.0
            </label>
            <label>
              <input
                type="radio"
                name="protocol"
                value="oidc"
                checked={protocol === 'oidc'}
                onChange={() => setProtocol('oidc')}
              />
              OpenID Connect
            </label>
          </div>
        </div>

        {protocol === 'saml' ? (
          <>
            <div className={styles.formGroup}>
              <label htmlFor="idpMetadataXml">IdP Metadata XML:</label>
              <textarea
                id="idpMetadataXml"
                value={idpMetadataXml}
                onChange={(e) => setIdpMetadataXml(e.target.value)}
                placeholder="Paste IdP metadata XML here..."
                rows={6}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="idpMetadataUrl">IdP Metadata URL:</label>
              <input
                id="idpMetadataUrl"
                type="text"
                value={idpMetadataUrl}
                onChange={(e) => setIdpMetadataUrl(e.target.value)}
                placeholder="https://idp.example.com/metadata (alternative to XML)"
              />
            </div>
          </>
        ) : (
          <>
            <div className={styles.formGroup}>
              <label htmlFor="oidcDiscoveryUrl">Discovery URL:</label>
              <input
                id="oidcDiscoveryUrl"
                type="text"
                value={oidcDiscoveryUrl}
                onChange={(e) => setOidcDiscoveryUrl(e.target.value)}
                placeholder="https://idp.example.com/.well-known/openid-configuration"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="oidcClientId">Client ID:</label>
              <input
                id="oidcClientId"
                type="text"
                value={oidcClientId}
                onChange={(e) => setOidcClientId(e.target.value)}
                placeholder="OIDC client ID"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="oidcClientSecret">Client Secret:</label>
              <input
                id="oidcClientSecret"
                type="password"
                value={oidcClientSecret}
                onChange={(e) => setOidcClientSecret(e.target.value)}
                placeholder={
                  config?.oidcClientSecret === '***'
                    ? 'Saved (leave blank to keep)'
                    : 'OIDC client secret'
                }
              />
            </div>
          </>
        )}

        <div className={styles.formGroup}>
          <label htmlFor="emailDomains">Email Domains:</label>
          <input
            id="emailDomains"
            type="text"
            value={emailDomains}
            onChange={(e) => setEmailDomains(e.target.value)}
            placeholder="uoft.ca, utoronto.ca"
          />
          <small>Comma-separated list of email domains for SSO discovery</small>
        </div>

        <div className={styles.formGroup}>
          <label>Auth Mode:</label>
          <div className={styles.radioRow}>
            <label>
              <input
                type="radio"
                name="authMode"
                value="exclusive"
                checked={authMode === 'exclusive'}
                onChange={() => setAuthMode('exclusive')}
              />
              Exclusive (SSO only)
            </label>
            <label>
              <input
                type="radio"
                name="authMode"
                value="supplementary"
                checked={authMode === 'supplementary'}
                onChange={() => setAuthMode('supplementary')}
              />
              Supplementary (SSO + password)
            </label>
          </div>
        </div>

        <details className={styles.advancedSection}>
          <summary>Attribute Mapping (Advanced)</summary>
          <div className={styles.formGroup}>
            <label htmlFor="mapEmail">Email attribute:</label>
            <input
              id="mapEmail"
              type="text"
              value={attrMapping.email}
              onChange={(e) => setAttrMapping({ ...attrMapping, email: e.target.value })}
              placeholder="email"
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="mapFirstName">First name attribute:</label>
            <input
              id="mapFirstName"
              type="text"
              value={attrMapping.firstName}
              onChange={(e) => setAttrMapping({ ...attrMapping, firstName: e.target.value })}
              placeholder="givenName / given_name"
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="mapLastName">Last name attribute:</label>
            <input
              id="mapLastName"
              type="text"
              value={attrMapping.lastName}
              onChange={(e) => setAttrMapping({ ...attrMapping, lastName: e.target.value })}
              placeholder="sn / family_name"
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="mapExtUid">External UID attribute:</label>
            <input
              id="mapExtUid"
              type="text"
              value={attrMapping.extUid}
              onChange={(e) => setAttrMapping({ ...attrMapping, extUid: e.target.value })}
              placeholder="eduPersonPrincipalName / sub"
            />
          </div>
        </details>

        <div className={styles.actionButtons}>
          <button type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          <button type="button" onClick={handleTest} disabled={!config && !saving}>
            Test Connection
          </button>
          {isActive && (
            <button type="button" onClick={handleDeactivate}>
              Deactivate
            </button>
          )}
          {(isPending || isDisabled) && (
            <button type="button" onClick={handleActivate}>
              Activate
            </button>
          )}
          <button type="button" onClick={handleDownloadMetadata}>
            Download SP Metadata
          </button>
          {config && (
            <button type="button" className={styles.deleteBtn} onClick={handleDelete}>
              Delete Configuration
            </button>
          )}
        </div>
      </div>
    </details>
  );
}

SsoConfigSection.propTypes = {
  orgId: PropTypes.number.isRequired,
  ssoEnabled: PropTypes.bool,
};
